import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import GroupIcon from '@mui/icons-material/Group';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import { registerOwner } from '../api';
import { PROPOSE_UI_COLORS } from '../theme';

const BUSINESS_TYPES = ['Club', 'Pub', 'Bar', 'Restaurant', 'Resort', 'Wine Shop', 'Event Organizer'];

const BENEFITS = [
  { icon: GroupIcon, title: 'More Customers', body: 'Get discovered by thousands of nearby nightlife seekers.' },
  { icon: TrendingUpIcon, title: 'Boost Revenue', body: 'Turn foot traffic into repeat customers with real offers.' },
  { icon: VerifiedIcon, title: 'Trusted Platform', body: 'Join a growing network of verified venues across the city.' }
];

// Ported from c2n-php ownerRegistration.php + controller/OwnerRegController.php (info=regResto).
// The legacy page also created a client-side Firebase account using the SMS'd promo code as the
// password; that step is dropped here since login now goes through the real /api/auth/login
// endpoint backed by a server-side password hash created during registration. Restyled to match
// the reference design's "Partner With Us" screen (benefits row + registration card).
const PartnerRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', name: '', businessType: 'Club', address: '', mobile: '', regCode: '', message: '' });
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setLoading(true);
    try {
      const result = await registerOwner({ email: form.email, name: form.name, address: form.address, mobile: form.mobile, regCode: form.regCode });
      const success = /successful/i.test(result.message);
      setMessage({ text: result.message, severity: success ? 'success' : 'error' });
      if (success) {
        setTimeout(() => navigate('/login'), 1500);
      }
    } catch {
      setMessage({ text: 'Registration failed. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '900px', mx: 'auto', px: { xs: 3, md: 6 }, pt: 14, pb: 8 }}>
      <Typography variant="h2" fontSize={36} mb={1}>Partner With Cheers2Night</Typography>
      <Typography color="text.secondary" fontSize={18} mb={5}>Grow Your Business. Reach More People.</Typography>

      <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr 1fr' }} gap={3} mb={6}>
        {BENEFITS.map(({ icon: Icon, title, body }) => (
          <Box key={title} sx={{ p: 3, borderRadius: 3, background: PROPOSE_UI_COLORS.surface, border: '1px solid rgba(245,246,248,0.08)' }}>
            <Icon sx={{ color: PROPOSE_UI_COLORS.primary, fontSize: 28, mb: 1.5 }} />
            <Typography fontWeight={700} mb={1}>{title}</Typography>
            <Typography color="text.secondary" fontSize={14}>{body}</Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: PROPOSE_UI_COLORS.surface,
          border: '1px solid rgba(245,246,248,0.08)',
          maxWidth: 560
        }}
      >
        <Typography variant="h6" mb={2}>Register Your Business</Typography>
        <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
          {message && <Alert severity={message.severity}>{message.text}</Alert>}
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
            <TextField label="Business Name" required value={form.name} onChange={handleChange('name')} />
            <TextField select label="Business Type" value={form.businessType} onChange={handleChange('businessType')}>
              {BUSINESS_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Box>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
            <TextField label="Mobile Number" required value={form.mobile} onChange={handleChange('mobile')} />
            <TextField label="Email Address" type="email" required value={form.email} onChange={handleChange('email')} />
          </Box>
          <TextField label="Address / City" required value={form.address} onChange={handleChange('address')} />
          <TextField label="Registration Code" required value={form.regCode} onChange={handleChange('regCode')} />
          <TextField label="Message (Optional)" multiline minRows={2} value={form.message} onChange={handleChange('message')} />
          <Button type="submit" variant="contained" color="primary" size="large" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Application'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerRegistrationPage;
